import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { ControllStatus, getRiskSourcesName, getRiskEventsName, getRiskConsequencesName } from 'src/api/types';
import { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { formatDate, numeralFormat } from 'src/utils/misc';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { IncompleteStateMarkerNew, renderNoData, TextOverflowTooltip } from 'src/common';
import { Link } from 'react-router-dom';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { ComparisonAssessmentQuery } from 'src/graphql/_generated';
import { TLLink } from 'src/pages/DashboardPage/comps';

type GqlScenario = NonNullable<ComparisonAssessmentQuery['assessmentA']>['scenarios'][0];

type UseColumns = (a: {
  data?: GqlScenario[];
  genPath: (a: GqlScenario) => string | undefined;
  showAllProjected?: boolean;
}) => {
  columns: ColumnDef<GqlScenario>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = [
  'name',
  'source',
  'event',
  'consequence',
  'expected_loss_base',
  'expected_loss',
  'expected_loss_projected',
  'updated_at',
];

export const useColumns: UseColumns = ({ data = [], genPath, showAllProjected }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.compareAssessments}-columns`,
    defaultColumns,
  );

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm];

    const expectedLossColoumns: ColumnDef<GqlScenario>[] = showAllProjected
      ? [
          {
            id: 'expected_loss_base',
            header: 'Unmodified Expected Loss',
            accessorFn: (row) => row.baseRisk?.expectedLoss || null,
            cell: (info) => {
              const value = info.getValue() as number;
              if (!value) return renderNoData();
              return numeralFormat(info.getValue() as number);
            },
          },
          {
            id: 'expected_loss',
            header: 'Live Expected Loss',
            accessorFn: (row) => row.liveRisk?.expectedLoss || null,
            cell: (info) => {
              const value = info.getValue() as number;
              if (!value) return renderNoData();
              return numeralFormat(info.getValue() as number);
            },
          },
          {
            id: 'expected_loss_projected',
            header: 'Projected Expected Loss',
            accessorFn: (row) => row.projectedRisk?.expectedLoss || null,
            cell: (info) => {
              const value = info.getValue() as number;
              if (!value) return renderNoData();
              return numeralFormat(info.getValue() as number);
            },
          },
        ]
      : [
          {
            id: 'expected_loss',
            header: 'Expected Loss',
            accessorFn: (row) => row.liveRisk?.expectedLoss || null,
            cell: (info) => {
              const value = info.getValue() as number;
              if (!value) return renderNoData();
              return numeralFormat(info.getValue() as number);
            },
          },
        ];

    const list: ColumnDef<GqlScenario>[] = [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: (info) => {
          const path = genPath(info.row.original);
          if (path) {
            return (
              <TextOverflowTooltip>
                <Link
                  className="table-link"
                  to={path}
                  onClick={(e) => {
                    e.stopPropagation();
                    mpEvent(MPEvents.Link, {
                      source: { value: ['Compare Assessments'] },
                      destination: { value: [path.startsWith('/compare') ? 'Compare Scenarios' : 'Scenario'] },
                    });
                  }}
                >
                  {info.getValue() as string}
                </Link>
              </TextOverflowTooltip>
            );
          }
          return <span>{info.getValue() as string}</span>;
        },
        sortUndefined: 'last',
        sortingFn: sortingFns.text,
      },
      {
        id: 'source',
        header: 'Source',
        accessorFn: (row) => getRiskSourcesName(row.source),
        cell: (info) => info.getValue(),
      },
      {
        id: 'event',
        header: 'Event',
        accessorFn: (row) => getRiskEventsName(row.event),
        cell: (info) => info.getValue(),
      },
      {
        id: 'consequence',
        header: 'Consequence',
        accessorFn: (row) => getRiskConsequencesName(row.consequence),
        cell: (info) => info.getValue(),
      },
      {
        id: 'management_strategy',
        header: 'Strategy',
        accessorFn: (row) => row.managementStrategy || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'risk_owner',
        header: 'Risk Owner',
        accessorFn: (row) => row.riskOwner?.text || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      ...expectedLossColoumns,
      // stuff here
      {
        id: '_control_count',
        header: 'Linked Controls',
        accessorFn: (row) => row.riskManagementPlans?.length || 0,
        cell: (info) => info.getValue(),
      },
      {
        id: '_control_count_live',
        header: 'Live Linked Controls',
        accessorFn: (row) => row.riskManagementPlans?.filter((el) => el.control?.status === ControllStatus.Live).length,
        cell: (info) => info.getValue(),
      },
      {
        id: '_modifiers',
        header: 'Risk Modifiers',
        accessorFn: (row) => {
          const mods = row.riskManagementPlans?.reduce((acc, el) => {
            if (el.modifiers) {
              acc += el.modifiers.length;
            }
            return acc;
          }, 0);

          return mods;
        },
        cell: (info) => info.getValue(),
      },
      {
        id: 'updated_at',
        header: 'Updated Date',
        accessorFn: (row) => row.meta?.updatedAt || null,
        cell: ({ row }) => {
          const date = row.original.meta?.updatedAt;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
        },
      },
      {
        id: 'edited_at',
        header: 'Last Edited Date',
        accessorFn: (row) => row.editedAt || null,
        cell: ({ row }) => formatDate(row.original.editedAt),
      },
      {
        id: 'created_at',
        header: 'Created Date',
        accessorFn: (row) => row.meta?.createdAt || null,
        cell: ({ row }) => {
          const date = row.original.meta?.createdAt;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm extraComponentLeft={<IncompleteStateMarkerNew scenario={row.original} />} />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, showAllProjected]);

  const expectedLossList = showAllProjected
    ? [
        {
          key: 'expected_loss_base',
          title: 'Unmodified Expected Loss',
        },
        {
          key: 'expected_loss',
          title: 'Live Expected Loss',
        },
        {
          key: 'expected_loss_projected',
          title: 'Projected Expected Loss',
        },
      ]
    : [
        {
          key: 'expected_loss',
          title: 'Expected Loss',
        },
      ];

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.compareAssessments}
      options={[
        { key: 'name', title: 'Name' },
        { key: 'source', title: 'Source' },
        { key: 'event', title: 'Event' },
        { key: 'consequence', title: 'Consequence' },
        {
          key: 'management_strategy',
          title: 'Strategy',
        },
        {
          key: 'risk_owner',
          title: 'Risk Owner',
        },
        ...expectedLossList,
        {
          key: '_control_count',
          title: 'Linked Controls',
        },
        {
          key: '_control_count_live',
          title: 'Live Linked Controls',
        },
        {
          key: '_modifiers',
          title: 'Risk Modifiers',
        },
        {
          key: 'updated_at',
          title: 'Updated Date',
        },
        {
          key: 'edited_at',
          title: 'Last Edited Date',
        },
        {
          key: 'created_at',
          title: 'Created Date',
        },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, GTColumnSelectAnchorExported };
};
